'use strict';

var _rollupPluginBabelHelpers = require('./_rollupPluginBabelHelpers-273b5552.js');
var React = require('react');
var styled = require('styled-components');
var elementOutline = require('./element-outline-e92b8ea2.js');
var focusVisibleSelector = require('./focus-visible-selector-7139d43f.js');
var tooltip = require('./tooltip-e34b5863.js');
var element = require('./element-2cae4d89.js');
var monosten = require('./monosten-d688040f.js');
var component = require('./component-21a580e6.js');
var element$1 = require('./element-2d0b0a9a.js');
var defaultTheme = require('./defaultTheme-068d5ee4.js');
var withStyles = require('./withStyles-b393a9eb.js');
var ReactDOM = require('react-dom');
var useIsFocusVisible = require('./useIsFocusVisible-5ebbc940.js');
var deprecatedPropType = require('./deprecatedPropType-e4925b44.js');
var capitalize = require('./capitalize-4d39daf3.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

function _interopNamespace(e) {
  if (e && e.__esModule) return e;
  var n = Object.create(null);
  if (e) {
    Object.keys(e).forEach(function (k) {
      if (k !== 'default') {
        var d = Object.getOwnPropertyDescriptor(e, k);
        Object.defineProperty(n, k, d.get ? d : {
          enumerable: true,
          get: function () {
            return e[k];
          }
        });
      }
    });
  }
  n['default'] = e;
  return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespace(React);
var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var styled__default = /*#__PURE__*/_interopDefaultLegacy(styled);
var ReactDOM__namespace = /*#__PURE__*/_interopNamespace(ReactDOM);

var useEnhancedEffect$1 = typeof window !== 'undefined' ? React__namespace.useLayoutEffect : React__namespace.useEffect;
/**
 * https://github.com/facebook/react/issues/14099#issuecomment-440013892
 *
 * @param {function} fn
 */

function useEventCallback(fn) {
  var ref = React__namespace.useRef(fn);
  useEnhancedEffect$1(function () {
    ref.current = fn;
  });
  return React__namespace.useCallback(function () {
    return (ref.current).apply(void 0, arguments);
  }, []);
}

/**
 * Given `this.props.children`, return an object mapping key to child.
 *
 * @param {*} children `this.props.children`
 * @return {object} Mapping of key to child
 */

function getChildMapping(children, mapFn) {
  var mapper = function mapper(child) {
    return mapFn && React.isValidElement(child) ? mapFn(child) : child;
  };

  var result = Object.create(null);
  if (children) React.Children.map(children, function (c) {
    return c;
  }).forEach(function (child) {
    // run the map function here instead so that the key is the computed one
    result[child.key] = mapper(child);
  });
  return result;
}
/**
 * When you're adding or removing children some may be added or removed in the
 * same render pass. We want to show *both* since we want to simultaneously
 * animate elements in and out. This function takes a previous set of keys
 * and a new set of keys and merges them with its best guess of the correct
 * ordering. In the future we may expose some of the utilities in
 * ReactMultiChild to make this easy, but for now React itself does not
 * directly have this concept of the union of prevChildren and nextChildren
 * so we implement it here.
 *
 * @param {object} prev prev children as returned from
 * `ReactTransitionChildMapping.getChildMapping()`.
 * @param {object} next next children as returned from
 * `ReactTransitionChildMapping.getChildMapping()`.
 * @return {object} a key set that contains all keys in `prev` and all keys
 * in `next` in a reasonable order.
 */

function mergeChildMappings(prev, next) {
  prev = prev || {};
  next = next || {};

  function getValueForKey(key) {
    return key in next ? next[key] : prev[key];
  } // For each key of `next`, the list of keys to insert before that key in
  // the combined list


  var nextKeysPending = Object.create(null);
  var pendingKeys = [];

  for (var prevKey in prev) {
    if (prevKey in next) {
      if (pendingKeys.length) {
        nextKeysPending[prevKey] = pendingKeys;
        pendingKeys = [];
      }
    } else {
      pendingKeys.push(prevKey);
    }
  }

  var i;
  var childMapping = {};

  for (var nextKey in next) {
    if (nextKeysPending[nextKey]) {
      for (i = 0; i < nextKeysPending[nextKey].length; i++) {
        var pendingNextKey = nextKeysPending[nextKey][i];
        childMapping[nextKeysPending[nextKey][i]] = getValueForKey(pendingNextKey);
      }
    }

    childMapping[nextKey] = getValueForKey(nextKey);
  } // Finally, add the keys which didn't appear before any key in `next`


  for (i = 0; i < pendingKeys.length; i++) {
    childMapping[pendingKeys[i]] = getValueForKey(pendingKeys[i]);
  }

  return childMapping;
}

function getProp(child, prop, props) {
  return props[prop] != null ? props[prop] : child.props[prop];
}

function getInitialChildMapping(props, onExited) {
  return getChildMapping(props.children, function (child) {
    return React.cloneElement(child, {
      onExited: onExited.bind(null, child),
      in: true,
      appear: getProp(child, 'appear', props),
      enter: getProp(child, 'enter', props),
      exit: getProp(child, 'exit', props)
    });
  });
}
function getNextChildMapping(nextProps, prevChildMapping, onExited) {
  var nextChildMapping = getChildMapping(nextProps.children);
  var children = mergeChildMappings(prevChildMapping, nextChildMapping);
  Object.keys(children).forEach(function (key) {
    var child = children[key];
    if (!React.isValidElement(child)) return;
    var hasPrev = (key in prevChildMapping);
    var hasNext = (key in nextChildMapping);
    var prevChild = prevChildMapping[key];
    var isLeaving = React.isValidElement(prevChild) && !prevChild.props.in; // item is new (entering)

    if (hasNext && (!hasPrev || isLeaving)) {
      // console.log('entering', key)
      children[key] = React.cloneElement(child, {
        onExited: onExited.bind(null, child),
        in: true,
        exit: getProp(child, 'exit', nextProps),
        enter: getProp(child, 'enter', nextProps)
      });
    } else if (!hasNext && hasPrev && !isLeaving) {
      // item is old (exiting)
      // console.log('leaving', key)
      children[key] = React.cloneElement(child, {
        in: false
      });
    } else if (hasNext && hasPrev && React.isValidElement(prevChild)) {
      // item hasn't changed transition states
      // copy over the last transition props;
      // console.log('unchanged', key)
      children[key] = React.cloneElement(child, {
        onExited: onExited.bind(null, child),
        in: prevChild.props.in,
        exit: getProp(child, 'exit', nextProps),
        enter: getProp(child, 'enter', nextProps)
      });
    }
  });
  return children;
}

var values = Object.values || function (obj) {
  return Object.keys(obj).map(function (k) {
    return obj[k];
  });
};

var defaultProps = {
  component: 'div',
  childFactory: function childFactory(child) {
    return child;
  }
};
/**
 * The `<TransitionGroup>` component manages a set of transition components
 * (`<Transition>` and `<CSSTransition>`) in a list. Like with the transition
 * components, `<TransitionGroup>` is a state machine for managing the mounting
 * and unmounting of components over time.
 *
 * Consider the example below. As items are removed or added to the TodoList the
 * `in` prop is toggled automatically by the `<TransitionGroup>`.
 *
 * Note that `<TransitionGroup>`  does not define any animation behavior!
 * Exactly _how_ a list item animates is up to the individual transition
 * component. This means you can mix and match animations across different list
 * items.
 */

var TransitionGroup = /*#__PURE__*/function (_React$Component) {
  withStyles._inheritsLoose(TransitionGroup, _React$Component);

  function TransitionGroup(props, context) {
    var _this;

    _this = _React$Component.call(this, props, context) || this;

    var handleExited = _this.handleExited.bind(withStyles._assertThisInitialized(_this)); // Initial children should all be entering, dependent on appear


    _this.state = {
      contextValue: {
        isMounting: true
      },
      handleExited: handleExited,
      firstRender: true
    };
    return _this;
  }

  var _proto = TransitionGroup.prototype;

  _proto.componentDidMount = function componentDidMount() {
    this.mounted = true;
    this.setState({
      contextValue: {
        isMounting: false
      }
    });
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    this.mounted = false;
  };

  TransitionGroup.getDerivedStateFromProps = function getDerivedStateFromProps(nextProps, _ref) {
    var prevChildMapping = _ref.children,
        handleExited = _ref.handleExited,
        firstRender = _ref.firstRender;
    return {
      children: firstRender ? getInitialChildMapping(nextProps, handleExited) : getNextChildMapping(nextProps, prevChildMapping, handleExited),
      firstRender: false
    };
  } // node is `undefined` when user provided `nodeRef` prop
  ;

  _proto.handleExited = function handleExited(child, node) {
    var currentChildMapping = getChildMapping(this.props.children);
    if (child.key in currentChildMapping) return;

    if (child.props.onExited) {
      child.props.onExited(node);
    }

    if (this.mounted) {
      this.setState(function (state) {
        var children = defaultTheme._extends({}, state.children);

        delete children[child.key];
        return {
          children: children
        };
      });
    }
  };

  _proto.render = function render() {
    var _this$props = this.props,
        Component = _this$props.component,
        childFactory = _this$props.childFactory,
        props = defaultTheme._objectWithoutPropertiesLoose(_this$props, ["component", "childFactory"]);

    var contextValue = this.state.contextValue;
    var children = values(this.state.children).map(childFactory);
    delete props.appear;
    delete props.enter;
    delete props.exit;

    if (Component === null) {
      return /*#__PURE__*/React__default['default'].createElement(tooltip.TransitionGroupContext.Provider, {
        value: contextValue
      }, children);
    }

    return /*#__PURE__*/React__default['default'].createElement(tooltip.TransitionGroupContext.Provider, {
      value: contextValue
    }, /*#__PURE__*/React__default['default'].createElement(Component, props, children));
  };

  return TransitionGroup;
}(React__default['default'].Component);

TransitionGroup.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * `<TransitionGroup>` renders a `<div>` by default. You can change this
   * behavior by providing a `component` prop.
   * If you use React v16+ and would like to avoid a wrapping `<div>` element
   * you can pass in `component={null}`. This is useful if the wrapping div
   * borks your css styles.
   */
  component: defaultTheme.PropTypes.any,

  /**
   * A set of `<Transition>` components, that are toggled `in` and out as they
   * leave. the `<TransitionGroup>` will inject specific transition props, so
   * remember to spread them through if you are wrapping the `<Transition>` as
   * with our `<Fade>` example.
   *
   * While this component is meant for multiple `Transition` or `CSSTransition`
   * children, sometimes you may want to have a single transition child with
   * content that you want to be transitioned out and in when you change it
   * (e.g. routes, images etc.) In that case you can change the `key` prop of
   * the transition child as you change its content, this will cause
   * `TransitionGroup` to transition the child out and back in.
   */
  children: defaultTheme.PropTypes.node,

  /**
   * A convenience prop that enables or disables appear animations
   * for all children. Note that specifying this will override any defaults set
   * on individual children Transitions.
   */
  appear: defaultTheme.PropTypes.bool,

  /**
   * A convenience prop that enables or disables enter animations
   * for all children. Note that specifying this will override any defaults set
   * on individual children Transitions.
   */
  enter: defaultTheme.PropTypes.bool,

  /**
   * A convenience prop that enables or disables exit animations
   * for all children. Note that specifying this will override any defaults set
   * on individual children Transitions.
   */
  exit: defaultTheme.PropTypes.bool,

  /**
   * You may need to apply reactive updates to a child as it is exiting.
   * This is generally done by using `cloneElement` however in the case of an exiting
   * child the element has already been removed and not accessible to the consumer.
   *
   * If you do need to update a child as it leaves you can provide a `childFactory`
   * to wrap every child, even the ones that are leaving.
   *
   * @type Function(child: ReactElement) -> ReactElement
   */
  childFactory: defaultTheme.PropTypes.func
} : {};
TransitionGroup.defaultProps = defaultProps;
var TransitionGroup$1 = TransitionGroup;

var useEnhancedEffect = typeof window === 'undefined' ? React__namespace.useEffect : React__namespace.useLayoutEffect;
/**
 * @ignore - internal component.
 */

function Ripple(props) {
  var classes = props.classes,
      _props$pulsate = props.pulsate,
      pulsate = _props$pulsate === void 0 ? false : _props$pulsate,
      rippleX = props.rippleX,
      rippleY = props.rippleY,
      rippleSize = props.rippleSize,
      inProp = props.in,
      _props$onExited = props.onExited,
      onExited = _props$onExited === void 0 ? function () {} : _props$onExited,
      timeout = props.timeout;

  var _React$useState = React__namespace.useState(false),
      leaving = _React$useState[0],
      setLeaving = _React$useState[1];

  var rippleClassName = withStyles.clsx(classes.ripple, classes.rippleVisible, pulsate && classes.ripplePulsate);
  var rippleStyles = {
    width: rippleSize,
    height: rippleSize,
    top: -(rippleSize / 2) + rippleY,
    left: -(rippleSize / 2) + rippleX
  };
  var childClassName = withStyles.clsx(classes.child, leaving && classes.childLeaving, pulsate && classes.childPulsate);
  var handleExited = useEventCallback(onExited); // Ripple is used for user feedback (e.g. click or press) so we want to apply styles with the highest priority

  useEnhancedEffect(function () {
    if (!inProp) {
      // react-transition-group#onExit
      setLeaving(true); // react-transition-group#onExited

      var timeoutId = setTimeout(handleExited, timeout);
      return function () {
        clearTimeout(timeoutId);
      };
    }

    return undefined;
  }, [handleExited, inProp, timeout]);
  return /*#__PURE__*/React__namespace.createElement("span", {
    className: rippleClassName,
    style: rippleStyles
  }, /*#__PURE__*/React__namespace.createElement("span", {
    className: childClassName
  }));
}

process.env.NODE_ENV !== "production" ? Ripple.propTypes = {
  /**
   * Override or extend the styles applied to the component.
   * See [CSS API](#css) below for more details.
   */
  classes: defaultTheme.PropTypes.object.isRequired,

  /**
   * @ignore - injected from TransitionGroup
   */
  in: defaultTheme.PropTypes.bool,

  /**
   * @ignore - injected from TransitionGroup
   */
  onExited: defaultTheme.PropTypes.func,

  /**
   * If `true`, the ripple pulsates, typically indicating the keyboard focus state of an element.
   */
  pulsate: defaultTheme.PropTypes.bool,

  /**
   * Diameter of the ripple.
   */
  rippleSize: defaultTheme.PropTypes.number,

  /**
   * Horizontal position of the ripple center.
   */
  rippleX: defaultTheme.PropTypes.number,

  /**
   * Vertical position of the ripple center.
   */
  rippleY: defaultTheme.PropTypes.number,

  /**
   * exit delay
   */
  timeout: defaultTheme.PropTypes.number.isRequired
} : void 0;

var DURATION = 550;
var DELAY_RIPPLE = 80;
var styles$2 = function styles(theme) {
  return {
    /* Styles applied to the root element. */
    root: {
      overflow: 'hidden',
      pointerEvents: 'none',
      position: 'absolute',
      zIndex: 0,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      borderRadius: 'inherit'
    },

    /* Styles applied to the internal `Ripple` components `ripple` class. */
    ripple: {
      opacity: 0,
      position: 'absolute'
    },

    /* Styles applied to the internal `Ripple` components `rippleVisible` class. */
    rippleVisible: {
      opacity: 0.3,
      transform: 'scale(1)',
      animation: "$enter ".concat(DURATION, "ms ").concat(theme.transitions.easing.easeInOut)
    },

    /* Styles applied to the internal `Ripple` components `ripplePulsate` class. */
    ripplePulsate: {
      animationDuration: "".concat(theme.transitions.duration.shorter, "ms")
    },

    /* Styles applied to the internal `Ripple` components `child` class. */
    child: {
      opacity: 1,
      display: 'block',
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      backgroundColor: 'currentColor'
    },

    /* Styles applied to the internal `Ripple` components `childLeaving` class. */
    childLeaving: {
      opacity: 0,
      animation: "$exit ".concat(DURATION, "ms ").concat(theme.transitions.easing.easeInOut)
    },

    /* Styles applied to the internal `Ripple` components `childPulsate` class. */
    childPulsate: {
      position: 'absolute',
      left: 0,
      top: 0,
      animation: "$pulsate 2500ms ".concat(theme.transitions.easing.easeInOut, " 200ms infinite")
    },
    '@keyframes enter': {
      '0%': {
        transform: 'scale(0)',
        opacity: 0.1
      },
      '100%': {
        transform: 'scale(1)',
        opacity: 0.3
      }
    },
    '@keyframes exit': {
      '0%': {
        opacity: 1
      },
      '100%': {
        opacity: 0
      }
    },
    '@keyframes pulsate': {
      '0%': {
        transform: 'scale(1)'
      },
      '50%': {
        transform: 'scale(0.92)'
      },
      '100%': {
        transform: 'scale(1)'
      }
    }
  };
};
/**
 * @ignore - internal component.
 *
 * TODO v5: Make private
 */

var TouchRipple = /*#__PURE__*/React__namespace.forwardRef(function TouchRipple(props, ref) {
  var _props$center = props.center,
      centerProp = _props$center === void 0 ? false : _props$center,
      classes = props.classes,
      className = props.className,
      other = defaultTheme._objectWithoutProperties(props, ["center", "classes", "className"]);

  var _React$useState = React__namespace.useState([]),
      ripples = _React$useState[0],
      setRipples = _React$useState[1];

  var nextKey = React__namespace.useRef(0);
  var rippleCallback = React__namespace.useRef(null);
  React__namespace.useEffect(function () {
    if (rippleCallback.current) {
      rippleCallback.current();
      rippleCallback.current = null;
    }
  }, [ripples]); // Used to filter out mouse emulated events on mobile.

  var ignoringMouseDown = React__namespace.useRef(false); // We use a timer in order to only show the ripples for touch "click" like events.
  // We don't want to display the ripple for touch scroll events.

  var startTimer = React__namespace.useRef(null); // This is the hook called once the previous timeout is ready.

  var startTimerCommit = React__namespace.useRef(null);
  var container = React__namespace.useRef(null);
  React__namespace.useEffect(function () {
    return function () {
      clearTimeout(startTimer.current);
    };
  }, []);
  var startCommit = React__namespace.useCallback(function (params) {
    var pulsate = params.pulsate,
        rippleX = params.rippleX,
        rippleY = params.rippleY,
        rippleSize = params.rippleSize,
        cb = params.cb;
    setRipples(function (oldRipples) {
      return [].concat(withStyles._toConsumableArray(oldRipples), [/*#__PURE__*/React__namespace.createElement(Ripple, {
        key: nextKey.current,
        classes: classes,
        timeout: DURATION,
        pulsate: pulsate,
        rippleX: rippleX,
        rippleY: rippleY,
        rippleSize: rippleSize
      })]);
    });
    nextKey.current += 1;
    rippleCallback.current = cb;
  }, [classes]);
  var start = React__namespace.useCallback(function () {
    var event = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var cb = arguments.length > 2 ? arguments[2] : undefined;
    var _options$pulsate = options.pulsate,
        pulsate = _options$pulsate === void 0 ? false : _options$pulsate,
        _options$center = options.center,
        center = _options$center === void 0 ? centerProp || options.pulsate : _options$center,
        _options$fakeElement = options.fakeElement,
        fakeElement = _options$fakeElement === void 0 ? false : _options$fakeElement;

    if (event.type === 'mousedown' && ignoringMouseDown.current) {
      ignoringMouseDown.current = false;
      return;
    }

    if (event.type === 'touchstart') {
      ignoringMouseDown.current = true;
    }

    var element = fakeElement ? null : container.current;
    var rect = element ? element.getBoundingClientRect() : {
      width: 0,
      height: 0,
      left: 0,
      top: 0
    }; // Get the size of the ripple

    var rippleX;
    var rippleY;
    var rippleSize;

    if (center || event.clientX === 0 && event.clientY === 0 || !event.clientX && !event.touches) {
      rippleX = Math.round(rect.width / 2);
      rippleY = Math.round(rect.height / 2);
    } else {
      var _ref = event.touches ? event.touches[0] : event,
          clientX = _ref.clientX,
          clientY = _ref.clientY;

      rippleX = Math.round(clientX - rect.left);
      rippleY = Math.round(clientY - rect.top);
    }

    if (center) {
      rippleSize = Math.sqrt((2 * Math.pow(rect.width, 2) + Math.pow(rect.height, 2)) / 3); // For some reason the animation is broken on Mobile Chrome if the size if even.

      if (rippleSize % 2 === 0) {
        rippleSize += 1;
      }
    } else {
      var sizeX = Math.max(Math.abs((element ? element.clientWidth : 0) - rippleX), rippleX) * 2 + 2;
      var sizeY = Math.max(Math.abs((element ? element.clientHeight : 0) - rippleY), rippleY) * 2 + 2;
      rippleSize = Math.sqrt(Math.pow(sizeX, 2) + Math.pow(sizeY, 2));
    } // Touche devices


    if (event.touches) {
      // check that this isn't another touchstart due to multitouch
      // otherwise we will only clear a single timer when unmounting while two
      // are running
      if (startTimerCommit.current === null) {
        // Prepare the ripple effect.
        startTimerCommit.current = function () {
          startCommit({
            pulsate: pulsate,
            rippleX: rippleX,
            rippleY: rippleY,
            rippleSize: rippleSize,
            cb: cb
          });
        }; // Delay the execution of the ripple effect.


        startTimer.current = setTimeout(function () {
          if (startTimerCommit.current) {
            startTimerCommit.current();
            startTimerCommit.current = null;
          }
        }, DELAY_RIPPLE); // We have to make a tradeoff with this value.
      }
    } else {
      startCommit({
        pulsate: pulsate,
        rippleX: rippleX,
        rippleY: rippleY,
        rippleSize: rippleSize,
        cb: cb
      });
    }
  }, [centerProp, startCommit]);
  var pulsate = React__namespace.useCallback(function () {
    start({}, {
      pulsate: true
    });
  }, [start]);
  var stop = React__namespace.useCallback(function (event, cb) {
    clearTimeout(startTimer.current); // The touch interaction occurs too quickly.
    // We still want to show ripple effect.

    if (event.type === 'touchend' && startTimerCommit.current) {
      event.persist();
      startTimerCommit.current();
      startTimerCommit.current = null;
      startTimer.current = setTimeout(function () {
        stop(event, cb);
      });
      return;
    }

    startTimerCommit.current = null;
    setRipples(function (oldRipples) {
      if (oldRipples.length > 0) {
        return oldRipples.slice(1);
      }

      return oldRipples;
    });
    rippleCallback.current = cb;
  }, []);
  React__namespace.useImperativeHandle(ref, function () {
    return {
      pulsate: pulsate,
      start: start,
      stop: stop
    };
  }, [pulsate, start, stop]);
  return /*#__PURE__*/React__namespace.createElement("span", defaultTheme._extends({
    className: withStyles.clsx(classes.root, className),
    ref: container
  }, other), /*#__PURE__*/React__namespace.createElement(TransitionGroup$1, {
    component: null,
    exit: true
  }, ripples));
});
process.env.NODE_ENV !== "production" ? TouchRipple.propTypes = {
  /**
   * If `true`, the ripple starts at the center of the component
   * rather than at the point of interaction.
   */
  center: defaultTheme.PropTypes.bool,

  /**
   * Override or extend the styles applied to the component.
   * See [CSS API](#css) below for more details.
   */
  classes: defaultTheme.PropTypes.object.isRequired,

  /**
   * @ignore
   */
  className: defaultTheme.PropTypes.string
} : void 0;
var TouchRipple$1 = withStyles.withStyles(styles$2, {
  flip: false,
  name: 'MuiTouchRipple'
})( /*#__PURE__*/React__namespace.memo(TouchRipple));

var styles$1 = {
  /* Styles applied to the root element. */
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    WebkitTapHighlightColor: 'transparent',
    backgroundColor: 'transparent',
    // Reset default value
    // We disable the focus ring for mouse, touch and keyboard users.
    outline: 0,
    border: 0,
    margin: 0,
    // Remove the margin in Safari
    borderRadius: 0,
    padding: 0,
    // Remove the padding in Firefox
    cursor: 'pointer',
    userSelect: 'none',
    verticalAlign: 'middle',
    '-moz-appearance': 'none',
    // Reset
    '-webkit-appearance': 'none',
    // Reset
    textDecoration: 'none',
    // So we take precedent over the style of a native <a /> element.
    color: 'inherit',
    '&::-moz-focus-inner': {
      borderStyle: 'none' // Remove Firefox dotted outline.

    },
    '&$disabled': {
      pointerEvents: 'none',
      // Disable link interactions
      cursor: 'default'
    },
    '@media print': {
      colorAdjust: 'exact'
    }
  },

  /* Pseudo-class applied to the root element if `disabled={true}`. */
  disabled: {},

  /* Pseudo-class applied to the root element if keyboard focused. */
  focusVisible: {}
};
/**
 * `ButtonBase` contains as few styles as possible.
 * It aims to be a simple building block for creating a button.
 * It contains a load of style reset and some focus/ripple logic.
 */

var ButtonBase = /*#__PURE__*/React__namespace.forwardRef(function ButtonBase(props, ref) {
  var action = props.action,
      buttonRefProp = props.buttonRef,
      _props$centerRipple = props.centerRipple,
      centerRipple = _props$centerRipple === void 0 ? false : _props$centerRipple,
      children = props.children,
      classes = props.classes,
      className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'button' : _props$component,
      _props$disabled = props.disabled,
      disabled = _props$disabled === void 0 ? false : _props$disabled,
      _props$disableRipple = props.disableRipple,
      disableRipple = _props$disableRipple === void 0 ? false : _props$disableRipple,
      _props$disableTouchRi = props.disableTouchRipple,
      disableTouchRipple = _props$disableTouchRi === void 0 ? false : _props$disableTouchRi,
      _props$focusRipple = props.focusRipple,
      focusRipple = _props$focusRipple === void 0 ? false : _props$focusRipple,
      focusVisibleClassName = props.focusVisibleClassName,
      onBlur = props.onBlur,
      onClick = props.onClick,
      onFocus = props.onFocus,
      onFocusVisible = props.onFocusVisible,
      onKeyDown = props.onKeyDown,
      onKeyUp = props.onKeyUp,
      onMouseDown = props.onMouseDown,
      onMouseLeave = props.onMouseLeave,
      onMouseUp = props.onMouseUp,
      onTouchEnd = props.onTouchEnd,
      onTouchMove = props.onTouchMove,
      onTouchStart = props.onTouchStart,
      onDragLeave = props.onDragLeave,
      _props$tabIndex = props.tabIndex,
      tabIndex = _props$tabIndex === void 0 ? 0 : _props$tabIndex,
      TouchRippleProps = props.TouchRippleProps,
      _props$type = props.type,
      type = _props$type === void 0 ? 'button' : _props$type,
      other = defaultTheme._objectWithoutProperties(props, ["action", "buttonRef", "centerRipple", "children", "classes", "className", "component", "disabled", "disableRipple", "disableTouchRipple", "focusRipple", "focusVisibleClassName", "onBlur", "onClick", "onFocus", "onFocusVisible", "onKeyDown", "onKeyUp", "onMouseDown", "onMouseLeave", "onMouseUp", "onTouchEnd", "onTouchMove", "onTouchStart", "onDragLeave", "tabIndex", "TouchRippleProps", "type"]);

  var buttonRef = React__namespace.useRef(null);

  function getButtonNode() {
    // #StrictMode ready
    return ReactDOM__namespace.findDOMNode(buttonRef.current);
  }

  var rippleRef = React__namespace.useRef(null);

  var _React$useState = React__namespace.useState(false),
      focusVisible = _React$useState[0],
      setFocusVisible = _React$useState[1];

  if (disabled && focusVisible) {
    setFocusVisible(false);
  }

  var _useIsFocusVisible = useIsFocusVisible.useIsFocusVisible(),
      isFocusVisible = _useIsFocusVisible.isFocusVisible,
      onBlurVisible = _useIsFocusVisible.onBlurVisible,
      focusVisibleRef = _useIsFocusVisible.ref;

  React__namespace.useImperativeHandle(action, function () {
    return {
      focusVisible: function focusVisible() {
        setFocusVisible(true);
        buttonRef.current.focus();
      }
    };
  }, []);
  React__namespace.useEffect(function () {
    if (focusVisible && focusRipple && !disableRipple) {
      rippleRef.current.pulsate();
    }
  }, [disableRipple, focusRipple, focusVisible]);

  function useRippleHandler(rippleAction, eventCallback) {
    var skipRippleAction = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : disableTouchRipple;
    return useEventCallback(function (event) {
      if (eventCallback) {
        eventCallback(event);
      }

      var ignore = skipRippleAction;

      if (!ignore && rippleRef.current) {
        rippleRef.current[rippleAction](event);
      }

      return true;
    });
  }

  var handleMouseDown = useRippleHandler('start', onMouseDown);
  var handleDragLeave = useRippleHandler('stop', onDragLeave);
  var handleMouseUp = useRippleHandler('stop', onMouseUp);
  var handleMouseLeave = useRippleHandler('stop', function (event) {
    if (focusVisible) {
      event.preventDefault();
    }

    if (onMouseLeave) {
      onMouseLeave(event);
    }
  });
  var handleTouchStart = useRippleHandler('start', onTouchStart);
  var handleTouchEnd = useRippleHandler('stop', onTouchEnd);
  var handleTouchMove = useRippleHandler('stop', onTouchMove);
  var handleBlur = useRippleHandler('stop', function (event) {
    if (focusVisible) {
      onBlurVisible(event);
      setFocusVisible(false);
    }

    if (onBlur) {
      onBlur(event);
    }
  }, false);
  var handleFocus = useEventCallback(function (event) {
    // Fix for https://github.com/facebook/react/issues/7769
    if (!buttonRef.current) {
      buttonRef.current = event.currentTarget;
    }

    if (isFocusVisible(event)) {
      setFocusVisible(true);

      if (onFocusVisible) {
        onFocusVisible(event);
      }
    }

    if (onFocus) {
      onFocus(event);
    }
  });

  var isNonNativeButton = function isNonNativeButton() {
    var button = getButtonNode();
    return component && component !== 'button' && !(button.tagName === 'A' && button.href);
  };
  /**
   * IE 11 shim for https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/repeat
   */


  var keydownRef = React__namespace.useRef(false);
  var handleKeyDown = useEventCallback(function (event) {
    // Check if key is already down to avoid repeats being counted as multiple activations
    if (focusRipple && !keydownRef.current && focusVisible && rippleRef.current && event.key === ' ') {
      keydownRef.current = true;
      event.persist();
      rippleRef.current.stop(event, function () {
        rippleRef.current.start(event);
      });
    }

    if (event.target === event.currentTarget && isNonNativeButton() && event.key === ' ') {
      event.preventDefault();
    }

    if (onKeyDown) {
      onKeyDown(event);
    } // Keyboard accessibility for non interactive elements


    if (event.target === event.currentTarget && isNonNativeButton() && event.key === 'Enter' && !disabled) {
      event.preventDefault();

      if (onClick) {
        onClick(event);
      }
    }
  });
  var handleKeyUp = useEventCallback(function (event) {
    // calling preventDefault in keyUp on a <button> will not dispatch a click event if Space is pressed
    // https://codesandbox.io/s/button-keyup-preventdefault-dn7f0
    if (focusRipple && event.key === ' ' && rippleRef.current && focusVisible && !event.defaultPrevented) {
      keydownRef.current = false;
      event.persist();
      rippleRef.current.stop(event, function () {
        rippleRef.current.pulsate(event);
      });
    }

    if (onKeyUp) {
      onKeyUp(event);
    } // Keyboard accessibility for non interactive elements


    if (onClick && event.target === event.currentTarget && isNonNativeButton() && event.key === ' ' && !event.defaultPrevented) {
      onClick(event);
    }
  });
  var ComponentProp = component;

  if (ComponentProp === 'button' && other.href) {
    ComponentProp = 'a';
  }

  var buttonProps = {};

  if (ComponentProp === 'button') {
    buttonProps.type = type;
    buttonProps.disabled = disabled;
  } else {
    if (ComponentProp !== 'a' || !other.href) {
      buttonProps.role = 'button';
    }

    buttonProps['aria-disabled'] = disabled;
  }

  var handleUserRef = useIsFocusVisible.useForkRef(buttonRefProp, ref);
  var handleOwnRef = useIsFocusVisible.useForkRef(focusVisibleRef, buttonRef);
  var handleRef = useIsFocusVisible.useForkRef(handleUserRef, handleOwnRef);

  var _React$useState2 = React__namespace.useState(false),
      mountedState = _React$useState2[0],
      setMountedState = _React$useState2[1];

  React__namespace.useEffect(function () {
    setMountedState(true);
  }, []);
  var enableTouchRipple = mountedState && !disableRipple && !disabled;

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React__namespace.useEffect(function () {
      if (enableTouchRipple && !rippleRef.current) {
        console.error(['Material-UI: The `component` prop provided to ButtonBase is invalid.', 'Please make sure the children prop is rendered in this custom component.'].join('\n'));
      }
    }, [enableTouchRipple]);
  }

  return /*#__PURE__*/React__namespace.createElement(ComponentProp, defaultTheme._extends({
    className: withStyles.clsx(classes.root, className, focusVisible && [classes.focusVisible, focusVisibleClassName], disabled && classes.disabled),
    onBlur: handleBlur,
    onClick: onClick,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
    onMouseDown: handleMouseDown,
    onMouseLeave: handleMouseLeave,
    onMouseUp: handleMouseUp,
    onDragLeave: handleDragLeave,
    onTouchEnd: handleTouchEnd,
    onTouchMove: handleTouchMove,
    onTouchStart: handleTouchStart,
    ref: handleRef,
    tabIndex: disabled ? -1 : tabIndex
  }, buttonProps, other), children, enableTouchRipple ?
  /*#__PURE__*/

  /* TouchRipple is only needed client-side, x2 boost on the server. */
  React__namespace.createElement(TouchRipple$1, defaultTheme._extends({
    ref: rippleRef,
    center: centerRipple
  }, TouchRippleProps)) : null);
});
process.env.NODE_ENV !== "production" ? ButtonBase.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // |     To update them edit the d.ts file and run "yarn proptypes"     |
  // ----------------------------------------------------------------------

  /**
   * A ref for imperative actions.
   * It currently only supports `focusVisible()` action.
   */
  action: tooltip.refType,

  /**
   * @ignore
   *
   * Use that prop to pass a ref to the native button component.
   * @deprecated Use `ref` instead.
   */
  buttonRef: deprecatedPropType.deprecatedPropType(tooltip.refType, 'Use `ref` instead.'),

  /**
   * If `true`, the ripples will be centered.
   * They won't start at the cursor interaction position.
   */
  centerRipple: defaultTheme.PropTypes.bool,

  /**
   * The content of the component.
   */
  children: defaultTheme.PropTypes.node,

  /**
   * Override or extend the styles applied to the component.
   * See [CSS API](#css) below for more details.
   */
  classes: defaultTheme.PropTypes.object,

  /**
   * @ignore
   */
  className: defaultTheme.PropTypes.string,

  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   */
  component: focusVisibleSelector.elementTypeAcceptingRef,

  /**
   * If `true`, the base button will be disabled.
   */
  disabled: defaultTheme.PropTypes.bool,

  /**
   * If `true`, the ripple effect will be disabled.
   *
   * ⚠️ Without a ripple there is no styling for :focus-visible by default. Be sure
   * to highlight the element by applying separate styles with the `focusVisibleClassName`.
   */
  disableRipple: defaultTheme.PropTypes.bool,

  /**
   * If `true`, the touch ripple effect will be disabled.
   */
  disableTouchRipple: defaultTheme.PropTypes.bool,

  /**
   * If `true`, the base button will have a keyboard focus ripple.
   */
  focusRipple: defaultTheme.PropTypes.bool,

  /**
   * This prop can help identify which element has keyboard focus.
   * The class name will be applied when the element gains the focus through keyboard interaction.
   * It's a polyfill for the [CSS :focus-visible selector](https://drafts.csswg.org/selectors-4/#the-focus-visible-pseudo).
   * The rationale for using this feature [is explained here](https://github.com/WICG/focus-visible/blob/master/explainer.md).
   * A [polyfill can be used](https://github.com/WICG/focus-visible) to apply a `focus-visible` class to other components
   * if needed.
   */
  focusVisibleClassName: defaultTheme.PropTypes.string,

  /**
   * @ignore
   */
  href: defaultTheme.PropTypes.string,

  /**
   * @ignore
   */
  onBlur: defaultTheme.PropTypes.func,

  /**
   * @ignore
   */
  onClick: defaultTheme.PropTypes.func,

  /**
   * @ignore
   */
  onDragLeave: defaultTheme.PropTypes.func,

  /**
   * @ignore
   */
  onFocus: defaultTheme.PropTypes.func,

  /**
   * Callback fired when the component is focused with a keyboard.
   * We trigger a `onFocus` callback too.
   */
  onFocusVisible: defaultTheme.PropTypes.func,

  /**
   * @ignore
   */
  onKeyDown: defaultTheme.PropTypes.func,

  /**
   * @ignore
   */
  onKeyUp: defaultTheme.PropTypes.func,

  /**
   * @ignore
   */
  onMouseDown: defaultTheme.PropTypes.func,

  /**
   * @ignore
   */
  onMouseLeave: defaultTheme.PropTypes.func,

  /**
   * @ignore
   */
  onMouseUp: defaultTheme.PropTypes.func,

  /**
   * @ignore
   */
  onTouchEnd: defaultTheme.PropTypes.func,

  /**
   * @ignore
   */
  onTouchMove: defaultTheme.PropTypes.func,

  /**
   * @ignore
   */
  onTouchStart: defaultTheme.PropTypes.func,

  /**
   * @ignore
   */
  tabIndex: defaultTheme.PropTypes.oneOfType([defaultTheme.PropTypes.number, defaultTheme.PropTypes.string]),

  /**
   * Props applied to the `TouchRipple` element.
   */
  TouchRippleProps: defaultTheme.PropTypes.object,

  /**
   * @ignore
   */
  type: defaultTheme.PropTypes.oneOfType([defaultTheme.PropTypes.oneOf(['button', 'reset', 'submit']), defaultTheme.PropTypes.string])
} : void 0;
var ButtonBase$1 = withStyles.withStyles(styles$1, {
  name: 'MuiButtonBase'
})(ButtonBase);

var styles = function styles(theme) {
  return {
    /* Styles applied to the root element. */
    root: defaultTheme._extends({}, theme.typography.button, {
      boxSizing: 'border-box',
      minWidth: 64,
      padding: '6px 16px',
      borderRadius: theme.shape.borderRadius,
      color: theme.palette.text.primary,
      transition: theme.transitions.create(['background-color', 'box-shadow', 'border'], {
        duration: theme.transitions.duration.short
      }),
      '&:hover': {
        textDecoration: 'none',
        backgroundColor: defaultTheme.alpha(theme.palette.text.primary, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        },
        '&$disabled': {
          backgroundColor: 'transparent'
        }
      },
      '&$disabled': {
        color: theme.palette.action.disabled
      }
    }),

    /* Styles applied to the span element that wraps the children. */
    label: {
      width: '100%',
      // Ensure the correct width for iOS Safari
      display: 'inherit',
      alignItems: 'inherit',
      justifyContent: 'inherit'
    },

    /* Styles applied to the root element if `variant="text"`. */
    text: {
      padding: '6px 8px'
    },

    /* Styles applied to the root element if `variant="text"` and `color="primary"`. */
    textPrimary: {
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: defaultTheme.alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        }
      }
    },

    /* Styles applied to the root element if `variant="text"` and `color="secondary"`. */
    textSecondary: {
      color: theme.palette.secondary.main,
      '&:hover': {
        backgroundColor: defaultTheme.alpha(theme.palette.secondary.main, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        }
      }
    },

    /* Styles applied to the root element if `variant="outlined"`. */
    outlined: {
      padding: '5px 15px',
      border: "1px solid ".concat(theme.palette.type === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)'),
      '&$disabled': {
        border: "1px solid ".concat(theme.palette.action.disabledBackground)
      }
    },

    /* Styles applied to the root element if `variant="outlined"` and `color="primary"`. */
    outlinedPrimary: {
      color: theme.palette.primary.main,
      border: "1px solid ".concat(defaultTheme.alpha(theme.palette.primary.main, 0.5)),
      '&:hover': {
        border: "1px solid ".concat(theme.palette.primary.main),
        backgroundColor: defaultTheme.alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        }
      }
    },

    /* Styles applied to the root element if `variant="outlined"` and `color="secondary"`. */
    outlinedSecondary: {
      color: theme.palette.secondary.main,
      border: "1px solid ".concat(defaultTheme.alpha(theme.palette.secondary.main, 0.5)),
      '&:hover': {
        border: "1px solid ".concat(theme.palette.secondary.main),
        backgroundColor: defaultTheme.alpha(theme.palette.secondary.main, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        }
      },
      '&$disabled': {
        border: "1px solid ".concat(theme.palette.action.disabled)
      }
    },

    /* Styles applied to the root element if `variant="contained"`. */
    contained: {
      color: theme.palette.getContrastText(theme.palette.grey[300]),
      backgroundColor: theme.palette.grey[300],
      boxShadow: theme.shadows[2],
      '&:hover': {
        backgroundColor: theme.palette.grey.A100,
        boxShadow: theme.shadows[4],
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          boxShadow: theme.shadows[2],
          backgroundColor: theme.palette.grey[300]
        },
        '&$disabled': {
          backgroundColor: theme.palette.action.disabledBackground
        }
      },
      '&$focusVisible': {
        boxShadow: theme.shadows[6]
      },
      '&:active': {
        boxShadow: theme.shadows[8]
      },
      '&$disabled': {
        color: theme.palette.action.disabled,
        boxShadow: theme.shadows[0],
        backgroundColor: theme.palette.action.disabledBackground
      }
    },

    /* Styles applied to the root element if `variant="contained"` and `color="primary"`. */
    containedPrimary: {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: theme.palette.primary.main
        }
      }
    },

    /* Styles applied to the root element if `variant="contained"` and `color="secondary"`. */
    containedSecondary: {
      color: theme.palette.secondary.contrastText,
      backgroundColor: theme.palette.secondary.main,
      '&:hover': {
        backgroundColor: theme.palette.secondary.dark,
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: theme.palette.secondary.main
        }
      }
    },

    /* Styles applied to the root element if `disableElevation={true}`. */
    disableElevation: {
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none'
      },
      '&$focusVisible': {
        boxShadow: 'none'
      },
      '&:active': {
        boxShadow: 'none'
      },
      '&$disabled': {
        boxShadow: 'none'
      }
    },

    /* Pseudo-class applied to the ButtonBase root element if the button is keyboard focused. */
    focusVisible: {},

    /* Pseudo-class applied to the root element if `disabled={true}`. */
    disabled: {},

    /* Styles applied to the root element if `color="inherit"`. */
    colorInherit: {
      color: 'inherit',
      borderColor: 'currentColor'
    },

    /* Styles applied to the root element if `size="small"` and `variant="text"`. */
    textSizeSmall: {
      padding: '4px 5px',
      fontSize: theme.typography.pxToRem(13)
    },

    /* Styles applied to the root element if `size="large"` and `variant="text"`. */
    textSizeLarge: {
      padding: '8px 11px',
      fontSize: theme.typography.pxToRem(15)
    },

    /* Styles applied to the root element if `size="small"` and `variant="outlined"`. */
    outlinedSizeSmall: {
      padding: '3px 9px',
      fontSize: theme.typography.pxToRem(13)
    },

    /* Styles applied to the root element if `size="large"` and `variant="outlined"`. */
    outlinedSizeLarge: {
      padding: '7px 21px',
      fontSize: theme.typography.pxToRem(15)
    },

    /* Styles applied to the root element if `size="small"` and `variant="contained"`. */
    containedSizeSmall: {
      padding: '4px 10px',
      fontSize: theme.typography.pxToRem(13)
    },

    /* Styles applied to the root element if `size="large"` and `variant="contained"`. */
    containedSizeLarge: {
      padding: '8px 22px',
      fontSize: theme.typography.pxToRem(15)
    },

    /* Styles applied to the root element if `size="small"`. */
    sizeSmall: {},

    /* Styles applied to the root element if `size="large"`. */
    sizeLarge: {},

    /* Styles applied to the root element if `fullWidth={true}`. */
    fullWidth: {
      width: '100%'
    },

    /* Styles applied to the startIcon element if supplied. */
    startIcon: {
      display: 'inherit',
      marginRight: 8,
      marginLeft: -4,
      '&$iconSizeSmall': {
        marginLeft: -2
      }
    },

    /* Styles applied to the endIcon element if supplied. */
    endIcon: {
      display: 'inherit',
      marginRight: -4,
      marginLeft: 8,
      '&$iconSizeSmall': {
        marginRight: -2
      }
    },

    /* Styles applied to the icon element if supplied and `size="small"`. */
    iconSizeSmall: {
      '& > *:first-child': {
        fontSize: 18
      }
    },

    /* Styles applied to the icon element if supplied and `size="medium"`. */
    iconSizeMedium: {
      '& > *:first-child': {
        fontSize: 20
      }
    },

    /* Styles applied to the icon element if supplied and `size="large"`. */
    iconSizeLarge: {
      '& > *:first-child': {
        fontSize: 22
      }
    }
  };
};
var Button = /*#__PURE__*/React__namespace.forwardRef(function Button(props, ref) {
  var children = props.children,
      classes = props.classes,
      className = props.className,
      _props$color = props.color,
      color = _props$color === void 0 ? 'default' : _props$color,
      _props$component = props.component,
      component = _props$component === void 0 ? 'button' : _props$component,
      _props$disabled = props.disabled,
      disabled = _props$disabled === void 0 ? false : _props$disabled,
      _props$disableElevati = props.disableElevation,
      disableElevation = _props$disableElevati === void 0 ? false : _props$disableElevati,
      _props$disableFocusRi = props.disableFocusRipple,
      disableFocusRipple = _props$disableFocusRi === void 0 ? false : _props$disableFocusRi,
      endIconProp = props.endIcon,
      focusVisibleClassName = props.focusVisibleClassName,
      _props$fullWidth = props.fullWidth,
      fullWidth = _props$fullWidth === void 0 ? false : _props$fullWidth,
      _props$size = props.size,
      size = _props$size === void 0 ? 'medium' : _props$size,
      startIconProp = props.startIcon,
      _props$type = props.type,
      type = _props$type === void 0 ? 'button' : _props$type,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'text' : _props$variant,
      other = defaultTheme._objectWithoutProperties(props, ["children", "classes", "className", "color", "component", "disabled", "disableElevation", "disableFocusRipple", "endIcon", "focusVisibleClassName", "fullWidth", "size", "startIcon", "type", "variant"]);

  var startIcon = startIconProp && /*#__PURE__*/React__namespace.createElement("span", {
    className: withStyles.clsx(classes.startIcon, classes["iconSize".concat(capitalize.capitalize(size))])
  }, startIconProp);
  var endIcon = endIconProp && /*#__PURE__*/React__namespace.createElement("span", {
    className: withStyles.clsx(classes.endIcon, classes["iconSize".concat(capitalize.capitalize(size))])
  }, endIconProp);
  return /*#__PURE__*/React__namespace.createElement(ButtonBase$1, defaultTheme._extends({
    className: withStyles.clsx(classes.root, classes[variant], className, color === 'inherit' ? classes.colorInherit : color !== 'default' && classes["".concat(variant).concat(capitalize.capitalize(color))], size !== 'medium' && [classes["".concat(variant, "Size").concat(capitalize.capitalize(size))], classes["size".concat(capitalize.capitalize(size))]], disableElevation && classes.disableElevation, disabled && classes.disabled, fullWidth && classes.fullWidth),
    component: component,
    disabled: disabled,
    focusRipple: !disableFocusRipple,
    focusVisibleClassName: withStyles.clsx(classes.focusVisible, focusVisibleClassName),
    ref: ref,
    type: type
  }, other), /*#__PURE__*/React__namespace.createElement("span", {
    className: classes.label
  }, startIcon, children, endIcon));
});
process.env.NODE_ENV !== "production" ? Button.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // |     To update them edit the d.ts file and run "yarn proptypes"     |
  // ----------------------------------------------------------------------

  /**
   * The content of the button.
   */
  children: defaultTheme.PropTypes.node,

  /**
   * Override or extend the styles applied to the component.
   * See [CSS API](#css) below for more details.
   */
  classes: defaultTheme.PropTypes.object,

  /**
   * @ignore
   */
  className: defaultTheme.PropTypes.string,

  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   */
  color: defaultTheme.PropTypes.oneOf(['default', 'inherit', 'primary', 'secondary']),

  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   */
  component: defaultTheme.PropTypes
  /* @typescript-to-proptypes-ignore */
  .elementType,

  /**
   * If `true`, the button will be disabled.
   */
  disabled: defaultTheme.PropTypes.bool,

  /**
   * If `true`, no elevation is used.
   */
  disableElevation: defaultTheme.PropTypes.bool,

  /**
   * If `true`, the  keyboard focus ripple will be disabled.
   */
  disableFocusRipple: defaultTheme.PropTypes.bool,

  /**
   * If `true`, the ripple effect will be disabled.
   *
   * ⚠️ Without a ripple there is no styling for :focus-visible by default. Be sure
   * to highlight the element by applying separate styles with the `focusVisibleClassName`.
   */
  disableRipple: defaultTheme.PropTypes.bool,

  /**
   * Element placed after the children.
   */
  endIcon: defaultTheme.PropTypes.node,

  /**
   * @ignore
   */
  focusVisibleClassName: defaultTheme.PropTypes.string,

  /**
   * If `true`, the button will take up the full width of its container.
   */
  fullWidth: defaultTheme.PropTypes.bool,

  /**
   * The URL to link to when the button is clicked.
   * If defined, an `a` element will be used as the root node.
   */
  href: defaultTheme.PropTypes.string,

  /**
   * The size of the button.
   * `small` is equivalent to the dense button styling.
   */
  size: defaultTheme.PropTypes.oneOf(['large', 'medium', 'small']),

  /**
   * Element placed before the children.
   */
  startIcon: defaultTheme.PropTypes.node,

  /**
   * @ignore
   */
  type: defaultTheme.PropTypes.oneOfType([defaultTheme.PropTypes.oneOf(['button', 'reset', 'submit']), defaultTheme.PropTypes.string]),

  /**
   * The variant to use.
   */
  variant: defaultTheme.PropTypes.oneOf(['contained', 'outlined', 'text'])
} : void 0;
var Button$1 = withStyles.withStyles(styles, {
  name: 'MuiButton'
})(Button);

/**
 * Mixin to create hover styling only on hover compatible devices.
 * Since mobile devices don't have a concept of hover, just having the
 * :hover selector will cause the style to get triggered on the first click
 * and not go away until something else on the screen is clicked (we call this sticky hover).
 *
 * To avoid this, use this mixin when you just want to define hover state on
 * hover compatible devices
 */
var stickyHoverMixin = "@media (hover: hover)";

var _templateObject$3, _templateObject2$1;

// These should be moved to somewhere common
var borderRadius = 8;
var shadowCharcoal20 = '4.0px 4.0px 8.0px 0px rgba(29, 29, 31, 0.2)';

// Button focus
var buttonBorderWidth = element.ownUpWhiteTheme.spacing(0.25) + 'px';
var buttonFocusWidth = buttonBorderWidth;
var buttonFocusInset = element.ownUpWhiteTheme.spacing(0.25) + 'px';
var buttonFocusRadius = '6px';

// General color and spacing
var buttonBackgroundSpacingHorizonatal = element.ownUpWhiteTheme.spacing(4) + 'px';
var buttonBackgroundSpacingVertical = element.ownUpWhiteTheme.spacing(2) + 'px';
var buttonBackgroundRadius = borderRadius + 'px';
var buttonShadow = shadowCharcoal20;
var actionButtonRadius = borderRadius * 3.5 + 'px';
var socialButtonBackgroundSpacingHorizonatal = element.ownUpWhiteTheme.spacing(3) + 'px';
var slimButtonBackgroundSpacingHorizonatal = element.ownUpWhiteTheme.spacing(2.5) + 'px';
var slimButtonBackgroundSpacingVertical = element.ownUpWhiteTheme.spacing(1.75) + 'px';

// Icon size and spacing
var buttonIconSize = '1.25rem';
var socialButtonIconSize = '2rem';
var buttonIconSpacing = element.ownUpWhiteTheme.spacing(1) + 'px';
var buttonBackgroundSpacingIcon = element.ownUpWhiteTheme.spacing(2.25) + 'px';
var socialButtonBackgroundSpacingIcon = element.ownUpWhiteTheme.spacing(1.5) + 'px';
var buttonSpacing = styled.css([":first-child{margin-left:", ";}:last-child{margin-right:", ";}"], buttonBackgroundSpacingHorizonatal, buttonBackgroundSpacingHorizonatal);
var slimButtonSpacing = styled.css([":first-child{margin-left:", ";}:last-child{margin-right:", ";}"], slimButtonBackgroundSpacingHorizonatal, slimButtonBackgroundSpacingHorizonatal);
var socialButtonSpacing = styled.css([":first-child{margin-left:", ";}:last-child{margin-right:", ";}:only-child{margin-left:", ";margin-right:", ";}"], socialButtonBackgroundSpacingHorizonatal, buttonBackgroundSpacingHorizonatal, socialButtonBackgroundSpacingHorizonatal, socialButtonBackgroundSpacingHorizonatal);
var IconStyle = styled__default['default'](component.OwnUpIcon)(_templateObject$3 || (_templateObject$3 = _rollupPluginBabelHelpers._taggedTemplateLiteral(["\n  display: inline-block;\n\n  ", "\n  margin: ", " ", ";\n\n  vertical-align: middle;\n  svg {\n    vertical-align: middle;\n    height: ", ";\n    width: ", ";\n  }\n\n  /* smaller spacing if icon with no text */\n  :only-child {\n    font-size: 0;\n    margin: ", " ", ";\n  }\n"])), buttonSpacing, buttonBackgroundSpacingIcon, buttonIconSpacing, buttonIconSize, buttonIconSize, buttonBackgroundSpacingIcon, buttonBackgroundSpacingIcon);
var TextStyle = styled__default['default'](element$1.OwnUpBodyMedium)(_templateObject2$1 || (_templateObject2$1 = _rollupPluginBabelHelpers._taggedTemplateLiteral(["\n  ", "\n  display: inline-block;\n  margin-top: ", ";\n  margin-bottom: ", ";\n  vertical-align: middle;\n"])), buttonSpacing, buttonBackgroundSpacingVertical, buttonBackgroundSpacingVertical);
var slimTextStyle = styled.css([".MuiTypography-root{", " ", " ", " margin-top:", ";margin-bottom:", ";text-transform:none;}"], monosten.aktivGroteskMediumMixin, element$1.smallBodyMixin, slimButtonSpacing, slimButtonBackgroundSpacingVertical, slimButtonBackgroundSpacingVertical);
var slimIconStyle = styled.css([".ou-icon{", " margin:", " ", ";margin-top:", ";margin-bottom:", ";vertical-align:middle;:only-child{font-size:0;margin:", ";}}"], slimButtonSpacing, slimButtonBackgroundSpacingVertical, buttonIconSpacing, slimButtonBackgroundSpacingVertical, slimButtonBackgroundSpacingVertical, buttonBackgroundSpacingVertical);
var baseButtonStyle = styled.css(["text-transform:unset;transition:unset;min-width:unset;margin:0;padding:0;font-size:0;box-sizing:border-box;border-radius:", ";"], buttonBackgroundRadius);
var actionButtonStyle = styled.css(["box-shadow:", ";border-radius:", ";&:focus-visible{&::after{border-radius:", ";}}"], buttonShadow, actionButtonRadius, actionButtonRadius);
var lineButtonHoverAndFocusStyle = styled.css(["background-color:", "FF;"], function (_ref) {
  var theme = _ref.theme;
  return theme.buttonColors[element.LINE_BUTTON_VARIANT].buttonBackgroundColorActive;
});
var lineButtonStyle = styled.css(["", " box-sizing:border-box;display:block;background-color:", "00;color:", ";box-shadow:", ";", "{&:hover:not(&[aria-disabled='true']){", " color:", "}}&", "{", " color:", ";}"], baseButtonStyle, function (_ref2) {
  var theme = _ref2.theme;
  return theme.buttonColors[element.LINE_BUTTON_VARIANT].buttonBackgroundColorActive;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.buttonColors[element.LINE_BUTTON_VARIANT].buttonFontColor;
}, function (_ref4) {
  var theme = _ref4.theme;
  return "0px 0px 0px ".concat(buttonFocusWidth, " ").concat(theme.buttonColors[element.LINE_BUTTON_VARIANT].buttonBorderColor, " inset");
}, stickyHoverMixin, lineButtonHoverAndFocusStyle, function (_ref5) {
  var theme = _ref5.theme;
  return theme.buttonColors[element.LINE_BUTTON_VARIANT].buttonFontColorHover;
}, focusVisibleSelector.focusVisibleSelectorMixin, lineButtonHoverAndFocusStyle, function (_ref6) {
  var theme = _ref6.theme;
  return theme.buttonColors[element.LINE_BUTTON_VARIANT].buttonFontColorFocus;
});
var socialButtonStyle = styled.css(["", " box-shadow:", ";color:", ";.ou-icon{", " margin:", " ", ";vertical-align:middle;svg{vertical-align:middle;height:", ";width:", ";}}"], baseButtonStyle, function (_ref7) {
  var theme = _ref7.theme;
  return "0px 0px 0px ".concat(buttonFocusWidth, " ").concat(theme.buttonColors[element.SOCIAL_BUTTON_VARIANT].buttonBorderColor, " inset");
}, function (_ref8) {
  var theme = _ref8.theme;
  return theme.buttonColors[element.SOCIAL_BUTTON_VARIANT].buttonFontColor;
}, socialButtonSpacing, socialButtonBackgroundSpacingIcon, buttonIconSpacing, socialButtonIconSize, socialButtonIconSize);

var _excluded$1 = ["icon", "iconPosition", "children", "disabledReason"];
var _templateObject$2;
/**
 * Any inactive buttons with tooltips need to be wrapped in a div so the
 *   tooltip has something to anchor itself to.
 *
 * Because the button stretches itself to take up the whole width of
 *   the parent container, and this is now the parent container, we
 *   need to also expand this container in those scenarios.
 *
 * See also: {@link https://v4.mui.com/components/tooltips/#disabled-elements}
 */
var TooltipWrapperDiv = styled__default['default'].div(_templateObject$2 || (_templateObject$2 = _rollupPluginBabelHelpers._taggedTemplateLiteral(["\n  display: inline-block;\n  ", " {\n    width: ", ";\n  }\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.breakpoints.down('sm');
}, function (_ref2) {
  var icon = _ref2.icon,
    buttonChildren = _ref2.buttonChildren;
  return icon && !buttonChildren ? '' : '100%';
});

/**
 * Icons and the text have different spacing depending on what is present.
 * material-ui adds another span under the parent Button, so need to add
 * the icons and text under a common element to determine which one gets
 * the proper spacing value.
 *
 * @param icon - (optional) The svg icon element
 * @param iconPosition - (optional) 'left' or 'right' to indicate icon's position
 * @param disabledReason - (optional) message to show in the disabled
 *    tooltip when disabled
 */
var OwnUpButton = function OwnUpButton(_ref3) {
  var icon = _ref3.icon,
    iconPosition = _ref3.iconPosition,
    children = _ref3.children,
    disabledReason = _ref3.disabledReason,
    props = _rollupPluginBabelHelpers._objectWithoutProperties(_ref3, _excluded$1);
  // if there is an onClick handler and the button is inactive, prevent action
  var onClick = React.useMemo(function () {
    return !!props.onClick && props.disabled ? function () {
      return;
    } : props.onClick;
  }, [props]);
  var buttonComponent = React.useMemo(function () {
    return /*#__PURE__*/React__default['default'].createElement(Button$1, _rollupPluginBabelHelpers._extends({}, _rollupPluginBabelHelpers._objectSpread2(_rollupPluginBabelHelpers._objectSpread2({}, props), {}, {
      onClick: onClick
    }), {
      disabled: false,
      "aria-disabled": props.disabled,
      "aria-describedby": "disabledReason"
    }), /*#__PURE__*/React__default['default'].createElement("span", null, icon && iconPosition !== 'right' && /*#__PURE__*/React__default['default'].createElement(IconStyle, {
      icon: icon
    }, icon), children && /*#__PURE__*/React__default['default'].createElement(TextStyle, {
      variant: "body1"
    }, children), icon && iconPosition === 'right' && /*#__PURE__*/React__default['default'].createElement(IconStyle, {
      icon: icon
    }, icon)));
  }, [children, icon, iconPosition, props, onClick]);
  var renderedComponent = React.useMemo(function () {
    return props.disabled && disabledReason ? /*#__PURE__*/React__default['default'].createElement(tooltip.OwnUpTooltip, {
      description: disabledReason,
      id: "disabledReason"
    }, /*#__PURE__*/React__default['default'].createElement(TooltipWrapperDiv, {
      icon: icon,
      buttonChildren: children
    }, buttonComponent)) : buttonComponent;
  }, [buttonComponent, children, disabledReason, icon, props.disabled]);
  return renderedComponent;
};

/**
 * Constructs the css for the specified button theme style
 * @param themeStyle e.g. 'fillButtonPrimaryTheme'
 */
var createOwnUpButtonThemeStyle = function createOwnUpButtonThemeStyle(themeStyle) {
  return styled.css(["", " color:", ";background:", ";&[aria-disabled='true']{color:", ";cursor:default;}", "{&:hover:not(&[aria-disabled='true']){background:", ";}}&", "{background:", ";", "}svg{path{fill:", ";}}", "{width:", ";}"], baseButtonStyle, function (_ref4) {
    var theme = _ref4.theme;
    return theme.buttonColors[themeStyle].buttonFontColor;
  }, function (_ref5) {
    var theme = _ref5.theme;
    return theme.buttonColors[themeStyle].buttonBackgroundColorActive;
  }, function (_ref6) {
    var theme = _ref6.theme;
    return theme.buttonColors[themeStyle].buttonFontColor;
  }, stickyHoverMixin, function (_ref7) {
    var theme = _ref7.theme;
    return theme.buttonColors[themeStyle].buttonBackgroundColorHover;
  }, focusVisibleSelector.focusVisibleSelectorMixin, function (_ref8) {
    var theme = _ref8.theme;
    return theme.buttonColors[themeStyle].buttonBackgroundColorFocus;
  }, function (_ref9) {
    var theme = _ref9.theme;
    return elementOutline.createElementOutlineMixin({
      color: theme.buttonColors[themeStyle].buttonFocusColor,
      width: buttonFocusWidth,
      inset: buttonFocusInset,
      radius: buttonFocusRadius
    });
  }, function (_ref10) {
    var theme = _ref10.theme;
    return theme.buttonColors[themeStyle].buttonIconColor;
  }, function (_ref11) {
    var theme = _ref11.theme;
    return theme.breakpoints.down('xs');
  }, function (_ref12) {
    var icon = _ref12.icon,
      children = _ref12.children;
    return icon && !children ? '' : '100%';
  });
};

var _templateObject$1, _templateObject2, _templateObject3, _templateObject4;
var fillButtonPrimaryVariantStyle = createOwnUpButtonThemeStyle(element.FILL_BUTTON_PRIMARY_VARIANT);
var OwnUpFillButtonPrimary = styled__default['default'](OwnUpButton)(_templateObject$1 || (_templateObject$1 = _rollupPluginBabelHelpers._taggedTemplateLiteral(["\n  ", "\n"])), fillButtonPrimaryVariantStyle);
var fillButtonPrimaryAccentVariantStyle = createOwnUpButtonThemeStyle(element.FILL_BUTTON_PRIMARY_ACCENT_VARIANT);
var OwnUpFillButtonPrimaryAccent = styled__default['default'](OwnUpButton)(_templateObject2 || (_templateObject2 = _rollupPluginBabelHelpers._taggedTemplateLiteral(["\n  ", "\n"])), fillButtonPrimaryAccentVariantStyle);
var fillButtonSecondaryThemeStyle = createOwnUpButtonThemeStyle(element.FILL_BUTTON_SECONDARY_VARIANT);
var OwnUpFillButtonSecondary = styled__default['default'](OwnUpButton)(_templateObject3 || (_templateObject3 = _rollupPluginBabelHelpers._taggedTemplateLiteral(["\n  ", "\n"])), fillButtonSecondaryThemeStyle);
var fillButtonSecondaryAccentThemeStyle = createOwnUpButtonThemeStyle(element.FILL_BUTTON_SECONDARY_ACCENT_VARIANT);
var OwnUpFillButtonSecondaryAccent = styled__default['default'](OwnUpButton)(_templateObject4 || (_templateObject4 = _rollupPluginBabelHelpers._taggedTemplateLiteral(["\n  ", "\n"])), fillButtonSecondaryAccentThemeStyle);

var _templateObject;
var _excluded = ["size", "name", "aria-label"],
  _excluded2 = ["buttonColors"];

/**
 * "size" is a reserved word on MUI icons and is used to generate
 *   different variants of the icon. We want to block that functionality
 *   and use the size solely for styles, so we strip it off the props
 *   before sending it to the wrapped component.
 *
 * Also attaches the required "name" field to relevant accessibility/scraper
 *   tags.
 *
 * @param {@see OwnUpIconButtonProps}
 */
var BaseIconButton = function BaseIconButton(_ref) {
  _ref.size;
    var name = _ref.name,
    ariaLabel = _ref['aria-label'],
    props = _rollupPluginBabelHelpers._objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React__default['default'].createElement(OwnUpButton, _rollupPluginBabelHelpers._extends({
    name: name,
    "aria-label": ariaLabel || name,
    "button-name": name,
    title: name
  }, props));
};
var determineIconSize = function determineIconSize(size, tokens) {
  if (size >= 2) {
    return tokens.iconSize;
  } else if (size > 1.125) {
    return tokens.iconSizeMicro;
  }
  return tokens.iconSizeMicroMicro;
};
var determineFocusSpacing = function determineFocusSpacing(size, tokens) {
  if (size >= 2.5) {
    return tokens.focusOutlineSpacingLargeMultiplier;
  } else if (size >= 1.5) {
    return tokens.focusOutlineSpacingMultiplier;
  }
  return tokens.focusOutlineSpacingMicroMultiplier;
};

/**
 * Helper function to generate the various color schemes for
 *   the icon buttons, as well as the base styling.
 */
var createStyledIconButton = function createStyledIconButton(variant) {
  return styled__default['default'](BaseIconButton)(_templateObject || (_templateObject = _rollupPluginBabelHelpers._taggedTemplateLiteral(["\n  ", "\n  ", "\n"])), fillButtonPrimaryVariantStyle, function (_ref2) {
    var _ref2$size = _ref2.size,
      size = _ref2$size === void 0 ? 3 : _ref2$size,
      _ref2$theme = _ref2.theme,
      iconButtons = _ref2$theme.buttonColors.iconButtons,
      theme = _rollupPluginBabelHelpers._objectWithoutProperties(_ref2$theme, _excluded2);
    var iconSize = determineIconSize(size, iconButtons);
    var focusSpacing = determineFocusSpacing(size, iconButtons);
    var colorScheme = iconButtons[variant] || iconButtons.defaultColors;
    return styled.css(["height:", "rem;width:", "rem;border-radius:", ";color:", ";background-color:", ";&[aria-disabled='true']{color:", ";cursor:default;}", "{&:hover:not(&[aria-disabled='true']),&[data-hover-added]{background-color:", ";}}&", "{background-color:", ";", "}svg{width:", ";height:", ";path{fill:", ";}}"], size, size, iconButtons.radius, colorScheme.color, colorScheme.backgroundColorActive, colorScheme.color, stickyHoverMixin, colorScheme.backgroundColorHover, focusVisibleSelector.focusVisibleSelectorMixin, colorScheme.backgroundColorFocus, elementOutline.createElementOutlineMixin({
      color: colorScheme.focusOutlineColor,
      width: "".concat(theme.spacing(iconButtons.focusOutlineSizeMultiplier), "px"),
      inset: "".concat(theme.spacing(focusSpacing), "px"),
      radius: iconButtons.radius
    }), iconSize, iconSize, colorScheme.color);
  });
};

/**
 * Default Own Up Icon Button. Wraps {@see OwnUpButton}
 *
 * @param {@see OwnUpIconButtonProps}
 */
var OwnUpIconButton = createStyledIconButton(element.ICON_BUTTON_DEFAULT_VARIANT);

/**
 * Accented variant of the Own Up Icon Button. Wraps {@see OwnUpButton}
 *
 * @param {@see OwnUpIconButtonProps}
 */
var OwnUpIconButtonAccent = createStyledIconButton(element.ICON_BUTTON_ACCENT_VARIANT);

/**
 * Quiet variant of the Own Up Icon Button. Wraps {@see OwnUpButton}
 *
 * @param {@see OwnUpIconButtonProps}
 */
var OwnUpIconButtonQuiet = createStyledIconButton(element.ICON_BUTTON_QUIET_VARIANT);

/**
 * Neutral variant of the Own Up Icon Button. Wraps {@see OwnUpButton}
 *
 * @param {@see OwnUpIconButtonProps}
 */
var OwnUpIconButtonNeutral = createStyledIconButton(element.ICON_BUTTON_NEUTRAL_VARIANT);

exports.ButtonBase = ButtonBase$1;
exports.OwnUpButton = OwnUpButton;
exports.OwnUpFillButtonPrimary = OwnUpFillButtonPrimary;
exports.OwnUpFillButtonPrimaryAccent = OwnUpFillButtonPrimaryAccent;
exports.OwnUpFillButtonSecondary = OwnUpFillButtonSecondary;
exports.OwnUpFillButtonSecondaryAccent = OwnUpFillButtonSecondaryAccent;
exports.OwnUpIconButton = OwnUpIconButton;
exports.OwnUpIconButtonAccent = OwnUpIconButtonAccent;
exports.OwnUpIconButtonNeutral = OwnUpIconButtonNeutral;
exports.OwnUpIconButtonQuiet = OwnUpIconButtonQuiet;
exports.actionButtonStyle = actionButtonStyle;
exports.baseButtonStyle = baseButtonStyle;
exports.buttonFocusInset = buttonFocusInset;
exports.buttonFocusRadius = buttonFocusRadius;
exports.buttonFocusWidth = buttonFocusWidth;
exports.createOwnUpButtonThemeStyle = createOwnUpButtonThemeStyle;
exports.fillButtonPrimaryVariantStyle = fillButtonPrimaryVariantStyle;
exports.lineButtonStyle = lineButtonStyle;
exports.slimIconStyle = slimIconStyle;
exports.slimTextStyle = slimTextStyle;
exports.socialButtonStyle = socialButtonStyle;
exports.stickyHoverMixin = stickyHoverMixin;
exports.useEventCallback = useEventCallback;
//# sourceMappingURL=icon-buttons-442762e4.js.map
